/* eslint-disable */

import { DATE_SQL_TYPES } from '@/features/Dashboard/utils/columnTypes';

/**
 * Chart Data Preparation Service
 *
 * Normalizes, validates, and prepares raw widget content for ApexCharts rendering.
 * Runs a 5-stage pipeline: normalize → merge duplicates → filter → sort → group "Other".
 */

// ── Per-chart-type default settings ────────────────────────────
export const CHART_TYPE_DEFAULTS = {
    pie:    { maxSlices: 10,       removeZeros: true,  removeNegatives: true,  sortOrder: 'desc' },
    donut:  { maxSlices: 10,       removeZeros: true,  removeNegatives: true,  sortOrder: 'desc' },
    bar:    { maxSlices: 15,       removeZeros: false, removeNegatives: false, sortOrder: 'desc' },
    column: { maxSlices: 20,       removeZeros: false, removeNegatives: false, sortOrder: 'none' },
    area:   { maxSlices: Infinity, removeZeros: false, removeNegatives: false, sortOrder: 'none' },
    line:   { maxSlices: Infinity, removeZeros: false, removeNegatives: false, sortOrder: 'none' },
    // Multi-series types shape their data via prepareMatrixData / prepareMetricSeriesData (not this
    // single-series path); the entries document that they preserve server row order and drop nothing.
    stackedColumn: { maxSlices: Infinity, removeZeros: false, removeNegatives: false, sortOrder: 'none' },
    dualAxisLine:  { maxSlices: Infinity, removeZeros: false, removeNegatives: false, sortOrder: 'none' },
    heatmap:       { maxSlices: Infinity, removeZeros: false, removeNegatives: false, sortOrder: 'none' },
    mixedComparison: { maxSlices: Infinity, removeZeros: false, removeNegatives: false, sortOrder: 'none' },
};

const FALLBACK_DEFAULTS = { maxSlices: Infinity, removeZeros: false, removeNegatives: false, sortOrder: 'none' };
const EMPTY_RESULT = { labels: [], series: [], colors: [] };

// Sentinel slice labels the pipeline injects: a null/blank group value becomes UNSET_LABEL
// (normalizeItems), and slices past maxSlices collapse into OTHER_LABEL (groupOther). Exported so
// downstream coloring can recognize + exclude them from a value-match — they are pipeline artifacts,
// not real categories. Single source of truth: consumers MUST import these, never re-literal them.
export const UNSET_LABEL = 'Unset';
export const OTHER_LABEL = 'Other';

// SSOT for the category-label convention: a raw group-by cell → its display label. Non-empty values
// are stringified + trimmed; null/blank collapses to the Unset bucket. Both the chart fill
// (normalizeItems below) and the 3D-isolate colour resolver (isolateColor.js) MUST key categories
// through THIS, so a value colours identically in the chart and in its isolate.
export function toCategoryLabel(raw, unsetLabel = UNSET_LABEL) {
    return (raw != null && String(raw).trim() !== '') ? String(raw).trim() : unsetLabel;
}

/**
 * Prepares raw content array for chart rendering.
 *
 * @param {Array}  rawContent   - The raw message.content array from the API
 * @param {string} chartType    - One of: 'bar','column','pie','donut','area','line'
 * @param {Object} [userOpts]   - Optional overrides
 * @param {number} [userOpts.maxSlices]       - Max items before grouping into "Other"
 * @param {boolean}[userOpts.removeZeros]     - Strip zero-value items
 * @param {boolean}[userOpts.removeNegatives] - Strip negative-value items
 * @param {boolean}[userOpts.mergeDuplicates] - Merge items with identical labels (default: true)
 * @param {string} [userOpts.sortOrder]       - 'desc', 'asc', or 'none'
 * @param {string} [userOpts.otherLabel]      - Label for grouped remainder (default: 'Other')
 * @param {string} [userOpts.unsetLabel]      - Label for null/empty labels (default: 'Unset')
 *
 * @returns {{ labels: string[], series: (number|null)[], colors: (string|null)[] }}
 */
export function prepareChartData(rawContent, chartType, userOpts = {}) {
    if (!rawContent || !Array.isArray(rawContent) || rawContent.length === 0) {
        return EMPTY_RESULT;
    }

    const defaults = CHART_TYPE_DEFAULTS[chartType] || FALLBACK_DEFAULTS;
    const opts = {
        ...defaults,
        mergeDuplicates: true,
        otherLabel: OTHER_LABEL,
        unsetLabel: UNSET_LABEL,
        ...userOpts,
    };

    // Stage 1: Normalize
    let items = normalizeItems(rawContent, opts.unsetLabel);

    if (items.length === 0) return EMPTY_RESULT;

    // Stage 2: Merge duplicate labels
    if (opts.mergeDuplicates) {
        items = mergeDuplicates(items);
    }

    // Stage 3: Filter invalid values (chart-type-dependent)
    items = filterItems(items, opts);

    if (items.length === 0) return EMPTY_RESULT;

    // Stage 4: Sort
    items = sortItems(items, opts.sortOrder);

    // Stage 5: Group small slices as "Other"
    items = groupOther(items, opts.maxSlices, opts.otherLabel);

    // Unzip into co-indexed arrays
    return {
        labels: items.map(i => i.label),
        series: items.map(i => i.value),
        colors: items.map(i => i.color),
    };
}

/**
 * The number each category shows ON the chart, as `{ [label]: value }` — the co-indexed
 * prepareChartData() labels/series unzipped back into a map.
 *
 * The 3D legend reports this beside its painted-geometry count so the two can never silently
 * disagree, and BOTH isolate entry points (a slice click and the widget header's "Isolate in 3D")
 * must read the same numbers. Non-numeric / gap datums are omitted rather than coerced to 0 — a
 * category with no reading has no charted number to quote. A widget that prepares nothing (a
 * multi-series matrix) yields {}, never invented numbers.
 */
export function preparedDatumValues(prepared) {
    var labels = Array.isArray(prepared && prepared.labels) ? prepared.labels : [];
    var series = Array.isArray(prepared && prepared.series) ? prepared.series : [];
    var datumValues = {};
    labels.forEach(function (label, index) {
        var datum = series[index];
        if (label == null || datum == null) return;
        var numeric = Number(datum);
        if (Number.isFinite(numeric)) datumValues[String(label).trim()] = numeric;
    });
    return datumValues;
}

/**
 * Chronologically orders a prepared single-series date trend without changing its raw labels.
 * Keeping ISO labels intact is required because chart selection sends those exact values back to
 * the dashboard filter contract. Invalid dates stay at the end in their original order.
 */
export function sortPreparedDateSeries(prepared, dataType) {
    if (!DATE_SQL_TYPES.includes(String(dataType || '').toLowerCase())) return prepared;
    const labels = Array.isArray(prepared && prepared.labels) ? prepared.labels : [];
    const series = Array.isArray(prepared && prepared.series) ? prepared.series : [];
    const colors = Array.isArray(prepared && prepared.colors) ? prepared.colors : [];
    const ordered = labels.map((label, index) => {
        const timestamp = Date.parse(label);
        return { label, value: series[index], color: colors[index], index, timestamp };
    }).sort((a, b) => {
        const aValid = Number.isFinite(a.timestamp);
        const bValid = Number.isFinite(b.timestamp);
        if (aValid && bValid) return a.timestamp - b.timestamp || a.index - b.index;
        if (aValid) return -1;
        if (bValid) return 1;
        return a.index - b.index;
    });
    return {
        ...prepared,
        labels: ordered.map(item => item.label),
        series: ordered.map(item => item.value),
        colors: ordered.map(item => item.color),
    };
}

// ── Stage 1: Normalize ─────────────────────────────────────────

function normalizeItems(rawContent, unsetLabel) {
    var result = [];
    for (var i = 0; i < rawContent.length; i++) {
        var item = rawContent[i];
        if (item == null) continue;

        // Handle both camelCase and PascalCase field names.
        // Use explicit null checks (not ||) so that value: 0 is preserved.
        var rawLabel = item.label !== undefined ? item.label : item.Label;
        var rawValue = item.value !== undefined ? item.value : item.Value;
        var rawColor = item.color !== undefined ? item.color : item.Color;

        // Coerce label to string (shared category-label convention — see toCategoryLabel)
        var label = toCategoryLabel(rawLabel, unsetLabel);

        // Coerce value to number. A null/NaN metric means "no reading" — keep it as
        // null (not 0) so downstream can render a gap instead of a misleading measured 0.
        var value = Number(rawValue);
        if (isNaN(value) || rawValue == null) {
            value = null;
        }

        // Color: keep as string or null
        var color = (rawColor != null && String(rawColor).trim() !== '')
            ? String(rawColor).trim()
            : null;

        result.push({ label: label, value: value, color: color });
    }
    return result;
}

// ── Shared value accumulation ──────────────────────────────────
// null = "no reading". Summing skips nulls instead of treating them as 0, but a
// group with no readings stays null (a gap) rather than collapsing to 0. A
// measured 0 is a real value and is preserved (0 == null is false).
function addValues(a, b) {
    if (a == null) return b;
    if (b == null) return a;
    return a + b;
}

// ── Stage 2: Merge duplicates ──────────────────────────────────

function mergeDuplicates(items) {
    var map = {};
    var order = [];

    for (var i = 0; i < items.length; i++) {
        var item = items[i];
        var key = item.label;

        if (map[key]) {
            map[key].value = addValues(map[key].value, item.value);
        } else {
            map[key] = { label: item.label, value: item.value, color: item.color };
            order.push(key);
        }
    }

    var result = [];
    for (var j = 0; j < order.length; j++) {
        result.push(map[order[j]]);
    }
    return result;
}

// ── Stage 3: Filter invalid values ─────────────────────────────

function filterItems(items, opts) {
    var result = [];
    for (var i = 0; i < items.length; i++) {
        var item = items[i];
        // null = "no reading". Drop it where zeros are dropped (pie/donut, removeZeros);
        // otherwise keep it so bar/column/line/area render a gap instead of a fake 0.
        if (item.value == null) {
            if (!opts.removeZeros) result.push(item);
            continue;
        }
        if (opts.removeNegatives && item.value < 0) continue;
        if (opts.removeZeros && item.value === 0) continue;
        result.push(item);
    }
    return result;
}

// ── Stage 4: Sort ──────────────────────────────────────────────

function sortItems(items, sortOrder) {
    if (sortOrder === 'none' || !sortOrder) return items;

    var sorted = items.slice(); // shallow copy to avoid mutating
    sorted.sort(function (a, b) {
        return sortOrder === 'desc' ? b.value - a.value : a.value - b.value;
    });
    return sorted;
}

// ── Stage 5: Group as "Other" ──────────────────────────────────

function groupOther(items, maxSlices, otherLabel) {
    if (!isFinite(maxSlices) || items.length <= maxSlices) return items;

    var kept = items.slice(0, maxSlices - 1);
    var rest = items.slice(maxSlices - 1);

    // Skip nulls when summing the remainder; if every grouped slice is null
    // (no readings), the "Other" bucket stays null (a gap) instead of a fake 0.
    var otherValue = null;
    for (var i = 0; i < rest.length; i++) {
        otherValue = addValues(otherValue, rest[i].value);
    }

    kept.push({ label: otherLabel, value: otherValue, color: null });
    return kept;
}

// ── Multi-series ("matrix") normalizer (stacked column, dual-axis, …) ──────────
// Typed error codes for a matrix widget whose aggregation config cannot produce an
// unambiguous category × series × value shape (Codex R1-2). The widget renders a
// config-error empty state on any of these rather than a silently-wrong chart.
export const MATRIX_ERROR = Object.freeze({
    MISSING_KEYS: 'missing-keys',       // category / series / value key absent or blank in the config
    DUPLICATE_KEYS: 'duplicate-keys',   // two of category / series / value resolve to the same column
    KEY_NOT_IN_DATA: 'key-not-in-data', // a resolved key is not a column on the returned rows
});

const EMPTY_MATRIX = { categories: [], series: [], possiblyTruncated: false };

// Own-property presence test — `key in obj` would false-positive on inherited Object.prototype
// names (a column literally named 'constructor'/'toString'), silently passing a bad key through.
function hasOwn(obj, key) {
    return Object.prototype.hasOwnProperty.call(obj, key);
}

// Resolve a groupBy entry to its column-name key. Persisted groupBy is a string[]; an object
// form ({ column | name | displayName }) is tolerated defensively (mirrors
// dashboard-widget.groupByColumn's extraction).
function matrixKey(entry) {
    if (entry == null) return '';
    if (typeof entry === 'string') return entry.trim();
    return String(entry.column || entry.name || entry.displayName || '').trim();
}

// Category / series display label: null / blank collapses to UNSET_LABEL and groups together, so a
// matrix labels its gaps the same way normalizeItems does for single-series charts.
function matrixLabel(value) {
    return (value != null && String(value).trim() !== '') ? String(value).trim() : UNSET_LABEL;
}

// Coerce a raw metric cell to a number, or null for "no reading" (mirrors normalizeItems: a
// null/NaN measure stays null so a missing combination and a measured 0 remain distinct).
function matrixValue(raw) {
    if (raw == null) return null;
    var num = Number(raw);
    return isNaN(num) ? null : num;
}

// True when there are no rows to shape: the widget shows its normal no-data state, never a
// config-error. Shared by both matrix normalizers.
function isEmptyRows(rows) {
    return !rows || !Array.isArray(rows) || rows.length === 0;
}

// Defensively-typed groupBy / metrics arrays from a widget's aggregation config, so both matrix
// normalizers read the same shape regardless of a malformed config.
function matrixAggregation(config) {
    var agg = (config && config.aggregation) || {};
    return {
        groupBy: Array.isArray(agg.groupBy) ? agg.groupBy : [],
        metrics: Array.isArray(agg.metrics) ? agg.metrics : [],
    };
}

// Cap-sentinel (R1-5): the wizard bakes TopBottom{count:1000} for matrix types, raising the server's
// aggregated row cap. When the returned row count equals that cap the result was (almost certainly)
// truncated server-side and low-value cells become indistinguishable from no-data cells; flag it so
// the widget can surface a "narrow your dimensions" notice.
function matrixTruncation(config, rowCount) {
    var cap = config && config.topBottom && Number(config.topBottom.count);
    return !!(cap && cap > 0 && rowCount === cap);
}

/**
 * Shape raw aggregate dict rows into a categories × series matrix for the multi-series chart
 * builders (stacked column, …). Pure — never mutates `rows` or `config`.
 *
 * Rows are the backend's raw-dict shape for a non-isChart messageType: each row carries every
 * groupBy column plus every metric alias as keys. The widget's aggregation config names the
 * three keys to read (Codex R1-2, strict): category = groupBy[0], series = groupBy[1],
 * value = metrics[0].alias. Category order follows the server row order (first-seen); a missing
 * category × series combination is null (a gap), distinct from a measured 0.
 *
 * @param {Array<Object>} rows raw dict rows from the widget query
 * @param {{ aggregation?: { groupBy?: Array, metrics?: Array }, topBottom?: { count?: number } }} config
 * @returns {{ categories: string[], series: Array<{ name: string, data: (number|null)[] }>, possiblyTruncated: boolean, error?: string }}
 *          On a config/key problem: the same shape with empty arrays plus an `error` code
 *          (MATRIX_ERROR) for the widget to render as a config-error state.
 */
export function prepareMatrixData(rows, config) {
    // Empty data is NOT an error — the widget shows its normal no-data state, never config-error.
    if (isEmptyRows(rows)) {
        return { ...EMPTY_MATRIX };
    }

    var { groupBy, metrics } = matrixAggregation(config);

    var categoryKey = matrixKey(groupBy[0]);
    var seriesKey = matrixKey(groupBy[1]);
    var valueKey = String((metrics[0] && metrics[0].alias) || '').trim();

    // Strict key resolution (R1-2): all three keys must exist and be distinct, else the dict rows
    // are ambiguous and we must not guess.
    if (!categoryKey || !seriesKey || !valueKey) {
        return { ...EMPTY_MATRIX, error: MATRIX_ERROR.MISSING_KEYS };
    }
    if (categoryKey === seriesKey || valueKey === categoryKey || valueKey === seriesKey) {
        return { ...EMPTY_MATRIX, error: MATRIX_ERROR.DUPLICATE_KEYS };
    }
    // The resolved keys must actually be columns on the returned rows (a blank/renamed alias would
    // otherwise silently map to undefined for every row). SELECT returns a uniform row shape, so
    // the first row is representative.
    var sample = rows[0];
    if (!sample || typeof sample !== 'object'
        || !hasOwn(sample, categoryKey) || !hasOwn(sample, seriesKey) || !hasOwn(sample, valueKey)) {
        return { ...EMPTY_MATRIX, error: MATRIX_ERROR.KEY_NOT_IN_DATA };
    }

    // Prototype-free maps so a category/series literally named 'constructor'/'__proto__' can't
    // collide with an inherited property during the first-seen lookup.
    var categories = [];
    var categoryIndex = Object.create(null); // category label → index in `categories`
    var seriesNames = [];
    var seriesIndex = Object.create(null);   // series label → index in `seriesNames`
    var cells = Object.create(null);         // `${catIdx}|${serIdx}` → value

    for (var i = 0; i < rows.length; i++) {
        var row = rows[i];
        if (row == null || typeof row !== 'object') continue;
        var catLabel = matrixLabel(row[categoryKey]);
        var serLabel = matrixLabel(row[seriesKey]);

        if (!(catLabel in categoryIndex)) {
            categoryIndex[catLabel] = categories.length;
            categories.push(catLabel);
        }
        if (!(serLabel in seriesIndex)) {
            seriesIndex[serLabel] = seriesNames.length;
            seriesNames.push(serLabel);
        }
        // GROUP BY makes each (category, series) unique, so a later write can only be a duplicate
        // combination (defensive) — last value wins, no summing.
        cells[`${categoryIndex[catLabel]}|${seriesIndex[serLabel]}`] = matrixValue(row[valueKey]);
    }

    var series = seriesNames.map(function (name, s) {
        return {
            name: name,
            // Missing combination → null (a gap), never 0. `in` distinguishes an absent cell from
            // a present null/0 value.
            data: categories.map(function (_unused, c) {
                var key = `${c}|${s}`;
                return key in cells ? cells[key] : null;
            }),
        };
    });

    var possiblyTruncated = matrixTruncation(config, rows.length);

    return { categories: categories, series: series, possiblyTruncated: possiblyTruncated };
}

/**
 * Shape raw aggregate dict rows into a categories × series matrix where each SERIES IS A METRIC
 * (dual-axis line, …) — as opposed to prepareMatrixData, whose series come from a second group-by.
 * Pure — never mutates `rows` or `config`.
 *
 * Category = groupBy[0]; one series per metric alias, data = that metric's value per category in
 * server row order. Strict R1-2: needs a category key and ≥2 non-blank, unique metric aliases, none
 * colliding with the category key, all present on the returned rows — else a typed error result.
 * Missing/null cells stay null (a gap), distinct from a measured 0.
 *
 * @param {Array<Object>} rows raw dict rows from the widget query
 * @param {{ aggregation?: { groupBy?: Array, metrics?: Array }, topBottom?: { count?: number } }} config
 * @returns {{ categories: string[], series: Array<{ name: string, data: (number|null)[] }>, possiblyTruncated: boolean, error?: string }}
 */
export function prepareMetricSeriesData(rows, config) {
    if (isEmptyRows(rows)) {
        return { ...EMPTY_MATRIX };
    }

    var { groupBy, metrics } = matrixAggregation(config);

    var categoryKey = matrixKey(groupBy[0]);
    var aliases = metrics.map(function (m) { return String((m && m.alias) || '').trim(); });

    // Need a category and ≥2 non-blank metric aliases.
    if (!categoryKey || aliases.length < 2 || aliases.some(function (a) { return !a; })) {
        return { ...EMPTY_MATRIX, error: MATRIX_ERROR.MISSING_KEYS };
    }
    // Aliases must be unique and distinct from the category key.
    var seenAlias = Object.create(null);
    for (var k = 0; k < aliases.length; k++) {
        if (aliases[k] === categoryKey || seenAlias[aliases[k]]) {
            return { ...EMPTY_MATRIX, error: MATRIX_ERROR.DUPLICATE_KEYS };
        }
        seenAlias[aliases[k]] = true;
    }
    // Category + every alias must be columns on the returned rows.
    var sample = rows[0];
    if (!sample || typeof sample !== 'object' || !hasOwn(sample, categoryKey)
        || aliases.some(function (a) { return !hasOwn(sample, a); })) {
        return { ...EMPTY_MATRIX, error: MATRIX_ERROR.KEY_NOT_IN_DATA };
    }

    var categories = [];
    var categoryIndex = Object.create(null);
    // One data array per metric alias, kept aligned to `categories` length as categories are seen.
    var seriesData = aliases.map(function () { return []; });

    for (var i = 0; i < rows.length; i++) {
        var row = rows[i];
        if (row == null || typeof row !== 'object') continue;
        var catLabel = matrixLabel(row[categoryKey]);
        if (!(catLabel in categoryIndex)) {
            categoryIndex[catLabel] = categories.length;
            categories.push(catLabel);
            for (var s = 0; s < aliases.length; s++) seriesData[s].push(null);
        }
        var ci = categoryIndex[catLabel];
        // GROUP BY groupBy[0] makes each category one row, so this writes once per category; a
        // duplicate category (defensive) just overwrites, last value wins.
        for (var m = 0; m < aliases.length; m++) {
            seriesData[m][ci] = matrixValue(row[aliases[m]]);
        }
    }

    var series = aliases.map(function (alias, idx) {
        return { name: alias, data: seriesData[idx] };
    });

    var possiblyTruncated = matrixTruncation(config, rows.length);

    return { categories: categories, series: series, possiblyTruncated: possiblyTruncated };
}

// Typed failures for the mixed-comparison contract. These distinguish an incomplete aggregation
// from a saved reference/metric alias that no longer exists in the returned aggregate dictionaries.
export const COMPARISON_ERROR = Object.freeze({
    MISSING_GROUP_BY: 'missing-group-by',
    MISSING_METRIC_ALIASES: 'missing-metric-aliases',
    DUPLICATE_METRIC_ALIASES: 'duplicate-metric-aliases',
    REFERENCE_ALIAS_NOT_FOUND: 'reference-alias-not-found',
    KEY_NOT_IN_DATA: 'key-not-in-data',
});

function comparisonContract(config) {
    var { groupBy, metrics } = matrixAggregation(config);
    return {
        groupKeys: groupBy.map(matrixKey),
        aliases: metrics.map(function (metric) { return String((metric && metric.alias) || '').trim(); }),
        referenceMetricAlias: String((config && config.comparison && config.comparison.referenceMetricAlias) || '').trim(),
    };
}

function comparisonConfigError(contract) {
    if (!contract.groupKeys.length || contract.groupKeys.some(function (key) { return !key; })) {
        return COMPARISON_ERROR.MISSING_GROUP_BY;
    }
    if (contract.aliases.length < 2 || contract.aliases.some(function (alias) { return !alias; })) {
        return COMPARISON_ERROR.MISSING_METRIC_ALIASES;
    }
    var aliasKeys = contract.aliases.map(function (alias) { return alias.toLowerCase(); });
    var groupKeySet = new Set(contract.groupKeys.map(function (key) { return key.toLowerCase(); }));
    if (new Set(aliasKeys).size !== aliasKeys.length || aliasKeys.some(function (alias) { return groupKeySet.has(alias); })) {
        return COMPARISON_ERROR.DUPLICATE_METRIC_ALIASES;
    }
    if (contract.referenceMetricAlias && !contract.aliases.includes(contract.referenceMetricAlias)) {
        return COMPARISON_ERROR.REFERENCE_ALIAS_NOT_FOUND;
    }
    return null;
}

function comparisonDataError(contract, sample) {
    if (!sample || typeof sample !== 'object') return COMPARISON_ERROR.KEY_NOT_IN_DATA;
    var requiredKeys = contract.groupKeys.concat(contract.aliases);
    return requiredKeys.some(function (key) { return !hasOwn(sample, key); })
        ? COMPARISON_ERROR.KEY_NOT_IN_DATA
        : null;
}

function hasExplicitTopBottom(config) {
    var topBottom = config && config.topBottom;
    var count = Number(topBottom && topBottom.count);
    var validMode = topBottom && (topBottom.mode === 'top' || topBottom.mode === 'bottom');
    var validColumn = topBottom && typeof topBottom.column === 'string' && topBottom.column.trim() !== '';
    return !!validMode && validColumn && Number.isInteger(count) && count > 0;
}

function comparisonTruncation(config, rowCount) {
    return hasExplicitTopBottom(config) ? false : matrixTruncation(config, rowCount);
}

function alignComparisonRows(rows, contract) {
    var categories = [];
    var seriesData = contract.aliases.map(function () { return []; });
    rows.forEach(function (row) {
        if (row == null || typeof row !== 'object') return;
        categories.push(contract.groupKeys.map(function (key) { return matrixLabel(row[key]); }).join(' · '));
        contract.aliases.forEach(function (alias, index) {
            seriesData[index].push(matrixValue(row[alias]));
        });
    });
    return {
        categories: categories,
        series: contract.aliases.map(function (alias, index) {
            return { name: alias, data: seriesData[index] };
        }),
    };
}

/**
 * Shape raw aggregate rows for the mixed comparison chart. Every configured group-by value forms
 * one composite display label, while each metric alias remains an index-aligned series. Pure: the
 * returned arrays are fresh and neither rows nor config are mutated.
 *
 * @param {Array<Object>} rows raw aggregate dictionaries
 * @param {{ aggregation?: { groupBy?: Array, metrics?: Array }, comparison?: { referenceMetricAlias?: string }, topBottom?: { count?: number } }} config
 * @returns {{ categories: string[], series: Array<{ name: string, data: (number|null)[] }>, referenceMetricAlias: string|null, possiblyTruncated: boolean, error?: string }}
 */
export function prepareComparisonData(rows, config) {
    var contract = comparisonContract(config);
    var configError = comparisonConfigError(contract);
    if (configError) {
        return { ...EMPTY_MATRIX, referenceMetricAlias: null, error: configError };
    }
    if (isEmptyRows(rows)) {
        return { ...EMPTY_MATRIX, referenceMetricAlias: null };
    }
    var dataError = comparisonDataError(contract, rows[0]);
    if (dataError) {
        return { ...EMPTY_MATRIX, referenceMetricAlias: null, error: dataError };
    }
    var aligned = alignComparisonRows(rows, contract);
    return {
        ...aligned,
        referenceMetricAlias: contract.referenceMetricAlias || null,
        possiblyTruncated: comparisonTruncation(config, rows.length),
    };
}
